import { cloneElement, isValidElement } from 'react';
import clsx from 'clsx';

import { styled, useThemeProps } from '@mui/material';
import { Breadcrumbs, Divider, generateUtilityClasses, Typography } from '@kc-console/common-ui';

import { RouterLink } from '../RouterLink';

import type { BreadcrumbsProps } from '@kc-console/common-ui';
import type { PageHeaderOwnerState, PageHeaderProps } from './types';

export default function PageHeader(props: PageHeaderProps) {
  const themedProps = useThemeProps({ name: 'PageHeader', props });
  const {
    breadcrumbs: breadcrumbsProp,
    description,
    extraActions,
    mainActions,
    showBottomDivider,
    title,
    ...others
  } = themedProps;

  const breadcrumbs = Array.isArray(breadcrumbsProp) ? (
    <Breadcrumbs
      className={pageHeaderClasses.breadcrumbs}
      items={breadcrumbsProp}
      slots={{ link: RouterLink }}
    />
  ) : isValidElement<BreadcrumbsProps>(breadcrumbsProp) ? (
    cloneElement(breadcrumbsProp, {
      ...breadcrumbsProp.props,
      className: clsx(pageHeaderClasses.breadcrumbs, breadcrumbsProp.props.className),
    })
  ) : (
    breadcrumbsProp
  );

  const ownerState: PageHeaderOwnerState = themedProps;

  return (
    <PageHeaderRoot ownerState={ownerState} {...others}>
      <PageHeaderMainContainer ownerState={ownerState}>
        <PageHeaderEssentials ownerState={ownerState}>
          {breadcrumbs}

          <PageHeaderTextContainer ownerState={ownerState}>
            <PageHeaderTitle variant="heading/semibold/3xl" component="h1" ownerState={ownerState}>
              {title}
            </PageHeaderTitle>

            {description && (
              <PageHeaderDescription
                variant="label/regular/sm"
                color="text/weaker/default"
                ownerState={ownerState}
              >
                {description}
              </PageHeaderDescription>
            )}
          </PageHeaderTextContainer>
        </PageHeaderEssentials>

        {mainActions && (
          <PageHeaderMainActions ownerState={ownerState}>{mainActions}</PageHeaderMainActions>
        )}
      </PageHeaderMainContainer>

      {extraActions && (
        <PageHeaderExtraActions ownerState={ownerState}>{extraActions}</PageHeaderExtraActions>
      )}

      {showBottomDivider && <PageHeaderBottomDivider ownerState={ownerState} />}
    </PageHeaderRoot>
  );
}

export const pageHeaderClasses = generateUtilityClasses('PageHeader', [
  'root',
  'mainContainer',
  'essentials',
  'breadcrumbs',
  'textContainer',
  'title',
  'description',
  'mainActions',
  'extraActions',
  'bottomDivider',
]);

const PageHeaderRoot = styled('div', {
  name: 'PageHeader',
  slot: 'root',
  target: pageHeaderClasses.root,
})<{ ownerState: PageHeaderOwnerState }>`
  display: flex;
  flex-direction: column;
`;

const PageHeaderMainContainer = styled('div', {
  name: 'PageHeader',
  slot: 'mainContainer',
  target: pageHeaderClasses.mainContainer,
})<{ ownerState: PageHeaderOwnerState }>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: nowrap;
`;

const PageHeaderEssentials = styled('div', {
  name: 'PageHeader',
  slot: 'essentials',
  target: pageHeaderClasses.essentials,
})<{ ownerState: PageHeaderOwnerState }>`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

const PageHeaderTextContainer = styled('div', {
  name: 'PageHeader',
  slot: 'textContainer',
  target: pageHeaderClasses.textContainer,
})<{ ownerState: PageHeaderOwnerState }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PageHeaderTitle = styled(Typography, {
  name: 'PageHeader',
  slot: 'title',
  target: pageHeaderClasses.title,
})<{ ownerState: PageHeaderOwnerState }>`
  white-space: pre-wrap;
  word-break: break-all;
`;

const PageHeaderDescription = styled(Typography, {
  name: 'PageHeader',
  slot: 'description',
  target: pageHeaderClasses.description,
})<{ ownerState: PageHeaderOwnerState }>`
  white-space: pre-wrap;
  word-break: break-all;
`;

const PageHeaderMainActions = styled('div', {
  name: 'PageHeader',
  slot: 'mainActions',
  target: pageHeaderClasses.mainActions,
})<{ ownerState: PageHeaderOwnerState }>``;

const PageHeaderExtraActions = styled('div', {
  name: 'PageHeader',
  slot: 'extraActions',
  target: pageHeaderClasses.extraActions,
})<{ ownerState: PageHeaderOwnerState }>`
  margin-top: 16px;
`;

const PageHeaderBottomDivider = styled(Divider, {
  name: 'PageHeader',
  slot: 'bottomDivider',
  target: pageHeaderClasses.bottomDivider,
})<{ ownerState: PageHeaderOwnerState }>`
  .${pageHeaderClasses.root} & {
    margin-top: 20px;
  }
`;
